<template>
  <section class="analytics-container">
    <Loading v-if="isLoading" />
    <template v-else>
      <section class="info-bar" v-if="$route.name === 'analyses' || $route.name === 'analytics'">
        <BarItem
          v-for="(item, index) in infoBarItems"
          :key="item.label"
          :isAmended="item.isAmended"
          :trendInfo="item.trendInfo"
          componentType="info"
          :text="item.text"
          :subText="item.subText"
          :hint="item.hint"
          :label="$t(item.label)"
          :iconUrl="item.iconUrl"
          :class="{ last: index === infoBarItems.length - 1, first: index === 0 }"
        />
      </section>
      <template v-if="$route.name === 'analyses' || $route.name === 'analytics'">
        <AnalyticsChartTable @chartClick="handleChartClick" @generalChartClick="handleGeneralChartClick" v-if="analyticsData.charts" :data="analyticsData.charts" />
        <TableByHour
          @hourClick="handleHourClick"
          v-if="analyticsData.hourData && analyticsData.hourData.length"
          :dataByHour="analyticsData.hourData"
          :hideRows="hourTableHideRowsWhenServikFiltered"
        />
      </template>
      <section
        v-if="$route.name === 'detailed' || $route.name === 'analytics'"
        :class="{ 'station-table': $route.name === 'analytics' }"
      >
        <RoomTable
          v-if="analyticsData.roomsData"
          :hideCols="computedHideCols"
          :rooms="analyticsData.roomsData"
          @click="handleTableClick"
        />
      </section>
    </template>
    <IframeCMP
      v-if="isPrinting"
      :infoBarItems="infoBarItems"
      :analyticsData="analyticsData.charts"
      :hourData="analyticsData.hourData"
      :hideInfo="hideInfo"
      :roomsData="analyticsData.roomsData"
      @donePrinting="$emit('donePrinting')"
      class="pdf-print-iframe"
    />
  </section>
</template>

<script>
import IframeCMP from './IframeCMP'
import BarItem from './BarItem'
import { infoBarItems } from '../consts/consts'
import { mapGetters } from 'vuex'
import {
  getOverviewSums,
  getAnalyticsSums,
  getRoomsCounterCallsSum,
  getRoomsCleandexCallsSum,
  getRoomsManualCallsSum,
  getRoomsUnnecessaryArrivals,
  isMetricShown,
  getUnnecessaryArrivalsShareText,
} from '../services/performanceService'
import {
  hasServikNameFilter,
  normalizeServikNames,
  resolveServikIdFromLabel,
} from '../../common/services/filterService'
import csvService from '../../common/services/csvService'
import performanceService from '../services/performanceService'
import utilService from '../../common/services/utilService'
import AnalyticsChartTable from './AnalyticsChartTable'
import RoomTable from './RoomTable'
import TableByHour from './TableByHour'
import Loading from '@/modules/common/components/Loading'
import { trendArrowColors } from '../consts/consts'

export default {
  name: 'analytics',
  props: {
    hideInfo: { default: () => [], type: Array, required: false },
    isPrinting: { type: Boolean, required: false },
  },
  data() {
    return {
      infoBarItems: JSON.parse(JSON.stringify(infoBarItems)),
      analyticsData: {},
      localLoading: false,
      filtersChanged: false,
      visibleColumns: [], // Track columns that should be visible from chart clicks
    }
  },
  created() {
    this.updatePageData()
    // Use a flag to prevent duplicate calls
    let isUpdating = false;
    // Watch for when both overviewData and analyticsData are available
    // Only trigger updatePageData once when both are ready
    this.$store.watch(
      (state) => ({
        overviewData: this.overviewData,
        analyticsData: this.$store.getters.analyticsData
      }),
      async ({ overviewData, analyticsData }) => {
        if (overviewData && analyticsData && !isUpdating) {
          isUpdating = true;
          try {
            await this.updatePageData();
          } finally {
            isUpdating = false;
          }
        }
      },
      { deep: false }
    )
  },
  methods: {
    async updateStoreData() {
      this.localLoading = true
      this.$store.commit({ type: 'toggleLoading', value: true })
      // Always use latest filterSelected from store, not cached computed value
      const latestFilter = this.$store.getters.filterSelected;
      await Promise.all([
        this.$store.dispatch({ type: 'getOverviewData', filter: latestFilter }),
        this.$store.dispatch({ type: 'getAnalyticsData', filter: latestFilter }),
      ])
      this.$store.commit({ type: 'toggleLoading', value: false })
      this.localLoading = false
    },
    handleHourClick(hour) {
      const dateToCalculateUTCHour = new Date()
      dateToCalculateUTCHour.setHours(+hour)
      this.$store.commit({ type: 'updateFilter', field: 'hour', value: hour })
      this.filtersChanged = true
      this.hideAdditionalFilter()
      this.updateStoreData()
    },
    handleChartClick(servikFilter) {
      const servikId = resolveServikIdFromLabel(
        servikFilter.label,
        this.$store.getters.filterOptions?.servikName || [],
      )
      if (servikId == null) return
      const list = normalizeServikNames(this.filterSelected?.servikName).map(Number)
      let next
      if (list.includes(servikId)) {
        next = list.filter((id) => id !== servikId)
        if (next.length === 0) next = null
      } else {
        next = [...list, servikId]
      }
      this.$store.commit({
        type: 'updateFilter',
        field: 'servikName',
        value: next,
      })
      this.filtersChanged = true
      this.hideAdditionalFilter()
      this.updateStoreData()
    },
    handleGeneralChartClick(chartData) {
      // chartData contains: { label, value, originalKey }
      const { originalKey } = chartData
      
      // Map original keys to column names
      const columnMap = {
        'totalLikes': 'likes',
        'maintenance': 'maintenanceComplaints'
      }
      
      const columnName = columnMap[originalKey]
      if (columnName) {
        // Toggle column visibility
        const index = this.visibleColumns.indexOf(columnName)
        if (index > -1) {
          // Column is visible, hide it
          this.visibleColumns.splice(index, 1)
        } else {
          // Column is hidden, show it
          this.visibleColumns.push(columnName)
        }
      }
    },
    updatePageData() {
      const data = JSON.parse(JSON.stringify(this.$store.getters.analyticsData))
      if (!data || !data.charts) return
      for (const key in data.charts) {
        if (!data.charts[key].data || !data.charts[key].labels) continue
        const sorted = utilService.sortParallelArrays(data.charts[key].data, data.charts[key].labels)
        data.charts[key].data = sorted.map((a) => a.num)
        data.charts[key].labels = sorted.map((a) => a.str)
      }
      this.refreshData(data)
      // Only sort cleaning data if it exists
      if (this.analyticsData.charts && this.analyticsData.charts.cleaning) {
        const res = utilService.sortParallelArrays(
          this.analyticsData.charts.cleaning.data,
          this.analyticsData.charts.cleaning.labels
        )
      }
    },
    async refreshData(data) {
      this.analyticsData = data
      //assign bar items data
      // Start from every card, so a card hidden by an earlier filter shows again once its value returns
      this.infoBarItems = JSON.parse(JSON.stringify(infoBarItems))
      this.infoBarItems[0].text = this.calcAvgScore(this.analyticsData.roomsData)
      const overviewSums = getOverviewSums(this.overviewData)

      const analyticsSums = getAnalyticsSums(this.analyticsData.hourData)
      analyticsSums.counterCalls = getRoomsCounterCallsSum(this.analyticsData.roomsData)
      analyticsSums.cleandexCalls = getRoomsCleandexCallsSum(this.analyticsData.roomsData)
      analyticsSums.manualCalls = getRoomsManualCallsSum(this.analyticsData.roomsData)
      // Bar visits follow the room table. The hour row is rounded per stat row, so it adds up to this total.
      if (this.analyticsData.roomsData && this.analyticsData.roomsData.length) {
        const sumRoomField = (key) => this.analyticsData.roomsData.reduce((sum, r) => sum + (Number(r[key]) || 0), 0)
        analyticsSums.visits = sumRoomField('visits')
        analyticsSums.arrivals = sumRoomField('arrivals')
      }

      // Check if any room has isArrivalsAmended
      if (this.analyticsData.roomsData && this.analyticsData.roomsData.some(room => room.isArrivalsAmended)) {
        analyticsSums.isArrivalsAmended = 1
      }

      // Shown at 0 too: no unnecessary arrivals is the result we want to see
      const unnecessaryArrivals = getRoomsUnnecessaryArrivals(this.analyticsData.roomsData)

      // Always use trendInfo from store - it's computed when overviewData is updated via computeTrendInfo
      // The store ensures trendInfo is computed once and cached in Vuex state
      // Add null check to prevent errors when switching companies (trendInfo might be null temporarily)
      const trendInfo = this.$store.getters.trendInfo || {};

      // to hide excluded info column & bar item
      if (this.hideInfo.length > 0)
        this.infoBarItems = this.infoBarItems.filter((col) => this.hideInfo.indexOf(col.name) === -1)
      this.infoBarItems = this.infoBarItems.reduce((acc, item) => {
        const isShown = isMetricShown(
          item.name,
          analyticsSums[item.name] || overviewSums[item.name],
          unnecessaryArrivals,
          this.filterSelected?.servikName
        )
        if (isShown) {
          // Safely access trendInfo property with null check
          item.trendInfo = trendInfo && trendInfo[item.name] ? trendInfo[item.name] : null;
          if (item.trendInfo && trendArrowColors[item.name]) {
            item.trendInfo.color = trendArrowColors[item.name][item.trendInfo.upDown]
          }

          if (item.name === 'visits') {
            item.isAmended = analyticsSums.isAmended
          }

          if (item.name === 'arrivals') {
            item.isAmended = analyticsSums.isArrivalsAmended
          }

          if (item.name === 'unnecessary_arrivals') {
            item.text = utilService.kFormatter(unnecessaryArrivals.count)
            item.subText = getUnnecessaryArrivalsShareText(unnecessaryArrivals, this.$t('of_arrivals'))
            item.hint = this.$t('unnecessary_arrivals_hint')
          } else if (item.name === 'avg_score' || item.name === 'response_time_SLA') {
            item.text = utilService.kMinutesToHours(analyticsSums[item.name] || overviewSums[item.name])
          } else if (item.name === 'exposures') {
            item.text = overviewSums[item.name] + '%'
          } else {
            item.text = utilService.kFormatter(analyticsSums[item.name] || overviewSums[item.name])
          }

          if (item.name === 'response_time_SLA') {
            item.text += ` ${this.$t('min')}`
          }
          acc.push(item)
        }
        return acc
      }, [])
      const barItemsInfo = csvService.getBarItemsInfoForCsv(this.infoBarItems)
      this.$store.commit('setBarItemsData', barItemsInfo)
    },
    handleTableClick(roomId) {
      // Set loading state before navigation to prevent showing stale data
      this.$store.commit({ type: 'toggleLoading', value: true })
      this.$router.push({ name: 'roomAnalyticsLog', params: { roomId } })
    },
    calcAvgScore(rooms) {
      if (!rooms) return console.warn('No rooms data')
      const totalScore = rooms.reduce((acc, room) => (acc += room.score), 0)
      let avgScore = totalScore / rooms.length
      return +avgScore.toFixed(0) === +avgScore.toFixed(1) ? avgScore.toFixed(0) : avgScore.toFixed(1)
    },
    async resetFilterAndGetData() {
      // Always use latest filterSelected from store
      const latestFilter = this.$store.getters.filterSelected;
      const { hour, servikName } = latestFilter
      this.$store.commit({ type: 'updateFilter', field: 'hour', value: hour })
      this.$store.commit({
        type: 'updateFilter',
        field: 'servikName',
        value: servikName,
      })

      this.localLoading = true
      this.$store.commit({ type: 'toggleLoading', value: true })
      // Get latest filter again after updates
      const updatedFilter = this.$store.getters.filterSelected;
      const analyticsPrm = this.$store.dispatch({
        type: 'getAnalyticsData',
        filter: updatedFilter,
      })
      const overviewPrm = this.$store.dispatch({
        type: 'getOverviewData',
        filter: updatedFilter,
      })
      await Promise.all([analyticsPrm, overviewPrm])
      this.$store.commit({ type: 'toggleLoading', value: false })
      this.localLoading = false
    },
    resetFilter() {
      this.$store.commit({ type: 'updateFilter', field: 'hour', value: null })
      this.$store.commit({
        type: 'updateFilter',
        field: 'servikName',
        value: null,
      })
      this.updateStoreData()
    },
    hideAdditionalFilter() {
      this.$store.commit({ type: 'toggleAdditionalFilter', value: false })
    },
  },
  computed: {
    ...mapGetters(['overviewData', 'isRTL', 'filterSelected', 'isLoading']),
    hourTableHideRowsWhenServikFiltered() {
      if (!hasServikNameFilter(this.filterSelected?.servikName)) return [];
      return ['arrivals', 'maintenanceComplaint', 'maintenanceArrivals', 'likes'];
    },
    computedHideCols() {
      // Default hidden columns (likes and maintenanceComplaints are hidden by default)
      const defaultHidden = ['likes', 'maintenanceComplaints']
      
      // Combine hideInfo with default hidden columns, removing duplicates
      const allHiddenCols = [...new Set([...this.hideInfo, ...defaultHidden])]
      
      // Remove columns that should be visible (user clicked to show them)
      return allHiddenCols.filter(col => !this.visibleColumns.includes(col))
    },
  },
  destroyed() {
    if (this.$route.path.includes('dashboard')) return
    if (this.filtersChanged) {
      this.resetFilter()
    }
  },
  components: {
    BarItem,
    AnalyticsChartTable,
    RoomTable,
    Loading,
    TableByHour,
    IframeCMP,
  },
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.info-bar {
  // Grid (not flex) guarantees every card gets the exact same column width,
  // regardless of how much text/content any single card holds. minmax()'s
  // first argument is a fixed length (not `auto`), so track sizing is never
  // inflated by a card's content - unlike flexbox, which can end up giving
  // different widths to items with different intrinsic content sizes.
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(150px, 1fr);
  align-items: stretch;
  gap: 12px;
  margin-bottom: 1.5vh;
  overflow-x: auto;
  padding-bottom: 4px;
  padding-top: 4px;
  scrollbar-width: thin;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
  
  > * {
    min-width: 0;
  }
}

.station-table {
  font-size: $font-size-smaller;
  margin-top: 3vh;
  @media print {
    margin-top: 50px;
  }
}

/* Off-screen + fixed width so PDF capture matches a wide desktop layout (not current vw after resize). */
.pdf-print-iframe {
  position: fixed;
  left: -12000px;
  top: 0;
  width: 1400px;
  min-width: 1400px;
  max-height: none;
  overflow: visible;
  visibility: hidden;
  pointer-events: none;
  z-index: -1;
}
</style>